<template lang="pug">
v-card.rounded-lg.pa-0.pa-md-6.mb-4(color="white" flat :min-height="minHeight")
  .text-lg.font-semibold.pa-6.pa-md-0 {{ title }}
  div(v-if="loading")
    v-skeleton-loader(type="table-row-divider@3")
  div(v-else)
    .text-sm(v-if="!isEmptyChartData && type !== 'custom'")
      .mb-1 For {{ periodText }},
      ul.mb-1(v-if="overview")
        template(v-if="overview.max")
          li The 
            span.font-semibold longest time patients spend in-clinic 
            span is at 
            span.font-semibold {{ overviewHighest }} 
            span which is  
            span.font-semibold {{ formatData(overview.max) }}
        template(v-if="overview.min")
          li The 
            span.font-semibold shortest time patients spend in-clinic 
            span is at 
            span.font-semibold {{ overviewLowest }} 
            span which is 
            span.font-semibold {{ formatData(overview.min) }}
        template(v-if="overview.avg")
          li The 
            span.font-semibold average time patients spend in-clinic 
            span is 
            span.font-semibold {{ formatData(overview.avg) }}
    v-card.mb-n8(flat)
      apexchart(type="bar" height="270" :options="chartOptions" :series="series")
      v-overlay.primary--text.text-center(v-if="isEmptyChartData" absolute color="white" opacity="0.9" z-index="2")
        v-icon.mb-2(color="primary" size=20) $yzi-bar-chart-10
        .h8.mb-1 No data available
        .text-sm There’s no data available yet for this chart
</template>

<script>
import { formatMinutes } from '@/utils'
import { DateTime } from 'luxon'

export default {
  name: 'ChartCard',
  props: {
    title: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: true
    },
    overview: {
      type: Object,
      default: () => {}
    },
    chartLabels: {
      type: Array,
      default: () => []
    },
    chartValues: {
      type: Array,
      default: () => []
    },
    timeframe: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: null
    },
    isToday: {
      type: Boolean,
      default: false,
    },
    currentDateRange: {
      type: Object,
      default: null,
    },
    colors: {
      type: Array,
      default: () => ['#00A699']
    }
  },
  data: () => ({
  }),
  computed: {
    periodText () {
      if (this.isToday) {
        return 'today'
      }
      return (this.currentDateRange?.name ?? '').toLowerCase()
    },
    series () {
      const data = this.chartValues ?? []
      return [{ 
        data,
        name: '',
      }]
    },
    minHeight () {
      return this.isCustom ? 360 : 450
    },
    isCustom () {
      return this.type === 'custom' 
    },
    chartOptions () {
      return {
        colors: this.colors,
        chart: {
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        xaxis: {
          labels: {
            formatter: (value) => {
              if (this.isCustom) {
                return value
              }
              const date = DateTime.fromISO(value)
              switch (this.timeframe) {
                case 'monthly':
                  return date.toFormat('MMM')
                case 'daily':
                  return date.toFormat('d')
                default:
                  return date.toFormat('h')
              }
            }
          },
          categories: this.chartLabels,
        },
        tooltip: {
          x: {
            formatter: (value) => {
              if (this.isCustom) {
                return value
              }
              const date = DateTime.fromISO(value)
              switch (this.timeframe) {
                case 'monthly':
                  return date.toFormat('MMM yyyy')
                default:
                  return date.toFormat('d MMM yyyy')
              }
            }
          },
          y: {
            formatter: (value) => {
              if (this.type === 'time') {
                return formatMinutes(value)
              } else if (this.isCustom) {
                return value
              }
              return value
            }
          },
        }
      }
    },
    isEmptyChartData () {
      return this.chartValues?.every(item => item === 0)
    },
    overviewHighest () {
      if (this.isCustom) {
        return 0
      }
      const max = this.overview?.max || 0
      return this.dataFromValue(max)
    },
    overviewLowest () {
      if (this.isCustom) {
        return 0
      }
      const min = this.overview?.min || 0
      return this.dataFromValue(min)
    },
  },
  methods: {
    formatData (value) {
      return formatMinutes(value)
    },
    dataFromValue (value) {
      const index = this.chartValues.findIndex(i => i === value)
      if (index >= 0 && value > 0) {
        if (this.isCustom) {
          return data
        }
        const data = this.chartLabels[index]
        return this.formatDateOverview(data)
      }
      return '-'
    },
    formatDateOverview (value) {
      const date = DateTime.fromISO(value)
      switch (this.timeframe) {
        case 'monthly':
          return date.toFormat('MMM yyyy')
        default:
          return date.toFormat('d MMM yyyy')
      }
    }
  }
}
</script>